/**
 * useDeepLinkReplay — Killed cold-boot deepLink race 해결.
 *
 * docs/97 v2.2 결정 #5:
 *   푸시 알림 탭 이벤트가 flush 되는 시점이
 *   authStore persist hydration 보다 빠를 수 있다. 이때 fcmPush 의 알림 탭 핸들러가
 *   path 를 usePendingDeepLinkStore.setPendingDeepLink(...) 로 큐잉. 본 effect 가
 *   hydration 완료 시점에 consume 해서 navigate.
 *
 * 동작:
 *   1) authStore persist 가 이미 hydrated 면 (정상 경로) — 즉시 1회 consume 시도.
 *   2) hydration 아직 미완이면 onFinishHydration subscribe → 완료 후 consume.
 *
 * 안전성:
 *   - 본 effect 는 RootLayout 안에서 1회 마운트.
 *   - consume 결과가 null (큐잉 없음 / TTL 만료) 이면 navigate 호출 없음.
 *   - hydration 후 navigate 가 protected route 가드에 막혀도 그건 인증된 사용자의 정상 흐름.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { usePendingDeepLinkStore } from "../store/pendingDeepLinkStore";

export function useDeepLinkReplay(): void {
  const navigate = useNavigate();

  useEffect(() => {
    const tryConsume = () => {
      const path = usePendingDeepLinkStore.getState().consumePendingDeepLink();
      if (!path) return;
      navigate(path);
    };

    // 1) 부트스트랩(/auth/refresh) 이미 settle → 즉시 시도.
    if (useAuthStore.getState().bootstrapped) {
      tryConsume();
      return;
    }

    // 2) 아직 부트스트랩 중 → settle 시점에 consume (persist hydration 대체).
    const unsub = useAuthStore.subscribe((state) => {
      if (state.bootstrapped) {
        unsub();
        tryConsume();
      }
    });
    // subscribe 직전 microtask 갭에서 bootstrapped 가 true 가 됐으면 위 구독은 영영 안 울린다 → 재확인.
    if (useAuthStore.getState().bootstrapped) {
      unsub();
      tryConsume();
    }
    return () => {
      unsub();
    };
  }, [navigate]);
}
